/**
 * StoryEditor - Main story generation interface with new compact layout
 * Left sidebar: settings and controls
 * Main area: two-column story grid
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/layout/Navbar.tsx";
import { Footer } from "@/components/layout/Footer.tsx";
import { StoryWizard } from "@/components/story/StoryWizard.tsx";
import { StoryPlayground } from "@/components/story/StoryPlayground.tsx";
import { StoryPlaygroundProvider } from "@/components/story/StoryPlaygroundContext.tsx";
import { useStoryGenerator } from "@/hooks/useStoryGenerator.ts";
import { useUserAuth } from "@/hooks/useUserAuth.ts";
import { Button } from "@/components/ui/button.tsx";
import { useToast } from "@/hooks/use-toast.ts";
import {
  getMyPublishedStories,
  type PublishedStory,
  publishStory,
  unpublishStory,
} from "@/lib/storage/stories.ts";
import { ExternalLink, Loader2 } from "lucide-react";
import type { Character, Encounter, Location, SideQuest } from "@/types/story.ts";

export function StoryEditor() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserAuth();
  const { toast } = useToast();
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedSnapshots, setPublishedSnapshots] = useState<PublishedStory[]>([]);
  const [isLoadingPublished, setIsLoadingPublished] = useState(false);
  const [unpublishingId, setUnpublishingId] = useState<string | null>(null);

  const {
    story,
    generatingFields,
    generationError,
    generateStep2,
    generateStep3,
    preloadStep2,
    preloadStep3,
    preloadStep4Bulk,
    generateFieldDraft,
    generateStep4Bulk,
    generateStoryNames,
    updateDraftOptions,
    selectSuggestion,
    removeSuggestion,
    clearDrafts,
    updateSettings,
    updateStoryName,
    updateStoryIntro,
    updateSideQuest,
    updateCharacter,
    updateLocation,
    updateEncounter,
    updateSlotCount,
    toggleLock,
    updateMainObjective,
    updateStoryTwist,
    generateSkillUses,
    lockInStep2,
    lockInStep3,
  } = useStoryGenerator(id || "");
  const storyId = story?.id ?? null;

  // Playground handlers
  const handleGenerateTwist = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<string[]> => {
    return generateFieldDraft("twist", { mode, forceFresh }) as Promise<string[]>;
  };

  const handleGenerateIntro = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<string[]> => {
    return generateFieldDraft("intro", { mode, forceFresh }) as Promise<string[]>;
  };

  const handleGenerateSideQuest = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<SideQuest[]> => {
    return generateFieldDraft("sideQuest", { mode, forceFresh }) as Promise<SideQuest[]>;
  };

  const handleGenerateCharacter = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<Character[]> => {
    return generateFieldDraft("character", { mode, forceFresh }) as Promise<Character[]>;
  };

  const handleGenerateLocation = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<Location[]> => {
    return generateFieldDraft("location", { mode, forceFresh }) as Promise<Location[]>;
  };

  const handleGenerateEncounter = (
    mode: "replace" | "append" = "replace",
    forceFresh = false,
  ): Promise<Encounter[]> => {
    return generateFieldDraft("encounter", { mode, forceFresh }) as Promise<Encounter[]>;
  };

  const handleGenerateMainObjective = (options?: { forceFresh?: boolean }) => {
    return generateStep3({ mode: "replace", forceFresh: options?.forceFresh });
  };

  const handleBackToWizard = () => {
    updateSettings({ generationStep: 3 });
  };

  const handlePublish = async () => {
    if (!story) return;

    setIsPublishing(true);
    try {
      const published = await publishStory(story.id);
      toast({
        title: t("publish.publishedTitle"),
        description: t("publish.publishedDescription"),
      });
      await refreshPublishedSnapshots();
      navigate(`/catalog/${published.slug}`);
    } catch (error) {
      toast({
        title: t("publish.publishFailedTitle"),
        description: error instanceof Error ? error.message : t("publish.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const refreshPublishedSnapshots = useCallback(async () => {
    if (!storyId || !isAuthenticated) {
      setPublishedSnapshots([]);
      return;
    }

    setIsLoadingPublished(true);
    try {
      const snapshots = await getMyPublishedStories(storyId);
      setPublishedSnapshots(snapshots);
    } finally {
      setIsLoadingPublished(false);
    }
  }, [storyId, isAuthenticated]);

  const handleUnpublish = async (publishedId: string) => {
    setUnpublishingId(publishedId);
    try {
      await unpublishStory(publishedId);
      toast({
        title: t("publish.unpublishedTitle"),
        description: t("publish.unpublishedDescription"),
      });
      await refreshPublishedSnapshots();
    } catch (error) {
      toast({
        title: t("publish.unpublishFailedTitle"),
        description: error instanceof Error ? error.message : t("publish.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setUnpublishingId(null);
    }
  };

  useEffect(() => {
    if (!story || !isAuthenticated) {
      setPublishedSnapshots([]);
      return;
    }
    void refreshPublishedSnapshots();
  }, [story?.id, isAuthenticated, refreshPublishedSnapshots]);

  if (!story) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">
              {t("common.loading", { defaultValue: "Loading..." })}
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isInWizard = story.settings.generationStep < 4;

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      {isAuthenticated && !isInWizard
        ? (
          <div className="mx-auto w-full max-w-6xl px-6 pt-4">
            <div className="flex flex-col items-end gap-2">
              <Button onClick={handlePublish} disabled={isPublishing} className="gap-2">
                {isPublishing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ExternalLink className="h-4 w-4" />}
                {isPublishing ? t("publish.publishing") : t("publish.publishToCatalogue")}
              </Button>

              {isLoadingPublished
                ? <p className="text-xs text-muted-foreground">{t("common.loading")}</p>
                : publishedSnapshots.length > 0
                ? (
                  <div className="rounded-md border border-border/70 bg-background/70 p-3 text-xs space-y-2 min-w-[280px]">
                    <p className="font-medium text-foreground">
                      {t("publish.publishedSnapshots", { count: publishedSnapshots.length })}
                    </p>
                    <div className="space-y-1">
                      {publishedSnapshots.slice(0, 3).map((snapshot) => (
                        <div key={snapshot.id} className="flex items-center justify-between gap-2">
                          <Link
                            to={`/catalog/${snapshot.slug}`}
                            className="text-accent underline underline-offset-2 truncate"
                          >
                            {new Date(snapshot.publishedAt).toLocaleString()}
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            disabled={unpublishingId === snapshot.id}
                            onClick={() =>
                              handleUnpublish(snapshot.id)}
                          >
                            {unpublishingId === snapshot.id
                              ? t("publish.unpublishing")
                              : t("publish.unpublish")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
                : (
                  <p className="text-xs text-muted-foreground">
                    {t("publish.noPublishedSnapshots")}
                  </p>
                )}
            </div>
          </div>
        )
        : null}

      {isInWizard
        ? (
          // Wizard view (steps 1-3)
          <StoryWizard
            settings={story.settings}
            drafts={story.drafts}
            generatingFields={generatingFields}
            generationError={generationError}
            onUpdateSettings={updateSettings}
            onGenerateStep2={generateStep2}
            onGenerateStep3={generateStep3}
            onPreloadStep2={preloadStep2}
            onPreloadStep3={preloadStep3}
            onPreloadStep4Bulk={preloadStep4Bulk}
            onLockInStep2={lockInStep2}
            onLockInStep3={lockInStep3}
            onRemoveSuggestion={removeSuggestion}
            onClearDrafts={clearDrafts}
            onBack={() => navigate("/")}
          />
        )
        : (
          // Playground view (step 4)
          <StoryPlaygroundProvider
            value={{
              settings: story.settings,
              storySeed: story.storySeed,
              drafts: story.drafts,
              onGenerateTwist: handleGenerateTwist,
              onGenerateIntro: handleGenerateIntro,
              onGenerateSideQuest: handleGenerateSideQuest,
              onGenerateCharacter: handleGenerateCharacter,
              onGenerateLocation: handleGenerateLocation,
              onGenerateEncounter: handleGenerateEncounter,
              onGenerateStoryNames: generateStoryNames,
              onGenerateMainObjective: handleGenerateMainObjective,
              onGenerateStep4Bulk: generateStep4Bulk,
              onUpdateDraftOptions: updateDraftOptions,
              onSelectSuggestion: selectSuggestion,
              onRemoveSuggestion: removeSuggestion,
              onToggleLock: toggleLock,
              onUpdateSettings: updateSettings,
              onUpdateStoryName: updateStoryName,
              onUpdateStoryIntro: updateStoryIntro,
              onUpdateSideQuest: updateSideQuest,
              onUpdateCharacter: updateCharacter,
              onUpdateLocation: updateLocation,
              onUpdateEncounter: updateEncounter,
              onUpdateSlotCount: updateSlotCount,
              onUpdateMainObjective: updateMainObjective,
              onUpdateStoryTwist: updateStoryTwist,
              onGenerateSkillUses: generateSkillUses,
              generatingFields,
              onBack: handleBackToWizard,
            }}
          >
            <StoryPlayground />
          </StoryPlaygroundProvider>
        )}

      <Footer />
    </div>
  );
}
